import { SeverityBadge } from '@sitewise/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { fetchVendorMe } from '../../api/vendor-auth.js';
import { fetchVendorWorkOrders, type VendorWorkOrderListItem } from '../../api/vendor-work-orders.js';

function Section({ title, items }: { title: string; items: VendorWorkOrderListItem[] }) {
  if (items.length === 0) return null;
  return (
    <section className="mb-6">
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">{title}</h2>
      <ul className="space-y-2">
        {items.map((wo) => (
          <li key={wo.id}>
            <Link
              to={`/vendor/work/${wo.id}`}
              className="block rounded-lg border border-border bg-surface p-4 active:bg-surface-muted"
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-base font-semibold text-fg">{wo.building.name}</p>
                  {wo.unit ? (
                    <p className="text-sm text-muted">Unit {wo.unit.label}</p>
                  ) : null}
                  <p className="mt-1 text-sm text-fg">{wo.title}</p>
                </div>
                <SeverityBadge level={wo.severity as 'low'} subtle className="shrink-0" />
              </div>
              <p className="mt-2 text-xs text-muted">
                {wo.displayNumber}
                {wo.dueAt ? ` · Due ${new Date(wo.dueAt).toLocaleDateString()}` : ''}
              </p>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function VendorWorkListPage() {
  const { data: me } = useQuery({ queryKey: ['vendor', 'me'], queryFn: fetchVendorMe });
  const { data, isLoading } = useQuery({
    queryKey: ['vendor', 'work-orders'],
    queryFn: fetchVendorWorkOrders,
  });

  if (isLoading) {
    return <p className="text-sm text-muted">Loading work…</p>;
  }

  const empty =
    !data?.today.length && !data?.thisWeek.length && !data?.later.length && !data?.completed.length;

  if (empty) {
    return (
      <p className="text-sm text-muted">
        Nothing assigned to {me?.company.name ?? 'your company'} right now.
      </p>
    );
  }

  return (
    <div>
      <h1 className="mb-4 text-lg font-semibold text-fg">Your work</h1>
      <Section title="Today" items={data?.today ?? []} />
      <Section title="This week" items={data?.thisWeek ?? []} />
      <Section title="Later" items={data?.later ?? []} />
      <Section title="Completed (last 30 days)" items={data?.completed ?? []} />
    </div>
  );
}
